import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  fetchAgentAuthUser,
  fetchAgentHoldings,
  fetchAgentSwaps,
  fetchAgentTvlSummary,
  fetchAgentWallet,
  fetchAgentWalletBalance,
  fetchVerifiedAgents,
  withdrawAgentSol,
  type AgentSwapEvent,
  type AgentTvlSummary,
  type PwaHolding,
  type VerifiedAgent
} from '../../utils/telegramAuth';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 16px 16px 80px 16px;
  box-sizing: border-box;
  color: #ffffff;
`;

const Card = styled.div`
  background: #0a0a0a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
`;

const HarvestHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
`;

const HarvestToggle = styled.button`
  background: transparent;
  border: none;
  color: #38bdf8;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #7dd3fc;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-bottom: 8px;
`;

const Subtle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
`;

const TotalUsdValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #10b981;
  text-align: center;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.45),
    0 0 12px rgba(16, 185, 129, 0.35);
`;

const BalanceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
`;

const BalanceValue = styled.div`
  font-size: 12px;
  font-weight: 600;
`;

const BalanceUsd = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const WalletAddressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const WalletAddressText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  word-break: break-all;
  text-align: center;
`;

const WalletAddressActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 4px;
`;

const WalletAddressAction = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 12px;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  padding: 12px 10px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const SectionLabel = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  padding: 10px 12px;
  color: #ffffff;
  font-size: 12px;
  box-sizing: border-box;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const HoldingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HoldingLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.06);
`;

const HoldingSymbol = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const HoldingRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const HoldingAmount = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const HoldingUsd = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const HoldingRoi = styled.div<{ $positive?: boolean | null }>`
  font-size: 12px;
  color: ${props => {
    if (props.$positive === true) return '#10b981';
    if (props.$positive === false) return '#ef4444';
    return 'rgba(255, 255, 255, 0.6)';
  }};
`;

const SwapLabel = styled.div<{ $direction: 'buy' | 'sell' }>`
  color: ${props => (props.$direction === 'buy' ? '#10b981' : '#ef4444')};
  font-weight: 600;
`;

const Label = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 12px 0;
`;

const SOL_MINT = 'So11111111111111111111111111111111111111112';

const formatAmount = (value: number) => {
  if (!Number.isFinite(value)) return '0';
  if (value >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
};

const formatUsd = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 1000 ? 0 : 2
  });
};

const SentryAgentWalletPWA: React.FC = () => {
  const navigate = useNavigate();
  const [agentName, setAgentName] = useState<string | null>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [harvestWalletAddress, setHarvestWalletAddress] = useState<string | null>(null);
  const [balanceSol, setBalanceSol] = useState<number | null>(null);
  const [holdings, setHoldings] = useState<PwaHolding[]>([]);
  const [swaps, setSwaps] = useState<AgentSwapEvent[]>([]);
  const [agentTvl, setAgentTvl] = useState<AgentTvlSummary | null>(null);
  const [verifiedAgents, setVerifiedAgents] = useState<VerifiedAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [solPriceUsd, setSolPriceUsd] = useState<number | null>(null);
  const [tokenPricesUsd, setTokenPricesUsd] = useState<Record<string, number>>({});
  const [copiedStatus, setCopiedStatus] = useState<string | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAll, setWithdrawAll] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [recentSwapsExpanded, setRecentSwapsExpanded] = useState(true);

  const totalUsd = useMemo(() => {
    if (solPriceUsd === null && holdings.length === 0) return null;
    const solUsd = (balanceSol ?? 0) * (solPriceUsd ?? 0);
    const tokenUsd = holdings.reduce((sum, holding) => {
      const price = tokenPricesUsd[holding.mintAddress] || 0;
      return sum + (holding.amount * price);
    }, 0);
    return solUsd + tokenUsd;
  }, [balanceSol, solPriceUsd, holdings, tokenPricesUsd]);

  const holdingsByMint = useMemo(() => {
    return new Map(holdings.map((holding) => [holding.mintAddress, holding]));
  }, [holdings]);

  const formatSwapTokenAmount = (swap: AgentSwapEvent) => {
    if (swap.token_amount !== null && swap.token_amount !== undefined) {
      return Number(swap.token_amount).toLocaleString(undefined, { maximumFractionDigits: 6 });
    }
    if (swap.amount_in && swap.direction === 'sell') {
      const base = Number(swap.amount_in);
      if (Number.isFinite(base)) {
        const decimals = holdingsByMint.get(swap.mint_address)?.decimals ?? 9;
        const amount = base / Math.pow(10, decimals);
        return amount.toLocaleString(undefined, { maximumFractionDigits: 6 });
      }
    }
    return null;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
      }
      const user = await fetchAgentAuthUser();
      if (!user) {
        navigate('/sentry-pwa');
        return;
      }
      if (!cancelled) {
        setAgentName(user.agentName);
      }
      const [wallet, balance, tokenHoldings, swapEvents, tvlSummary, verified] = await Promise.all([
        fetchAgentWallet(),
        fetchAgentWalletBalance(),
        fetchAgentHoldings(),
        fetchAgentSwaps(),
        fetchAgentTvlSummary(),
        fetchVerifiedAgents()
      ]);
      if (cancelled) return;
      setWalletAddress(wallet?.walletAddress || null);
      setHarvestWalletAddress(wallet?.harvestWalletAddress || null);
      setBalanceSol(balance?.balanceSol ?? null);
      setHoldings(tokenHoldings || []);
      setSwaps(swapEvents || []);
      setAgentTvl(tvlSummary);
      setVerifiedAgents(verified || []);
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const loadPrices = async () => {
      try {
        const solPrice = await getRelayTokenPriceSolana(SOL_MINT);
        if (!cancelled) {
          setSolPriceUsd(solPrice);
        }
      } catch {
        if (!cancelled) {
          setSolPriceUsd(null);
        }
      }

      if (!holdings.length) {
        if (!cancelled) {
          setTokenPricesUsd({});
        }
        return;
      }

      const uniqueMints = Array.from(new Set(holdings.map((h) => h.mintAddress)));
      const entries = await Promise.all(
        uniqueMints.map(async (mint) => {
          try {
            const price = await getRelayTokenPriceSolana(mint);
            return [mint, price] as const;
          } catch {
            return [mint, 0] as const;
          }
        })
      );
      if (!cancelled) {
        setTokenPricesUsd(Object.fromEntries(entries));
      }
    };
    loadPrices();
    return () => {
      cancelled = true;
    };
  }, [holdings]);

  const copyAddress = async (address: string | null, label: string) => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopiedStatus(`${label} copied`);
      setTimeout(() => setCopiedStatus(null), 2000);
    } catch {
      // ignore
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      const [wallet, balance, tokenHoldings, swapEvents, tvlSummary, verified] = await Promise.all([
        fetchAgentWallet(),
        fetchAgentWalletBalance(),
        fetchAgentHoldings(),
        fetchAgentSwaps(),
        fetchAgentTvlSummary(),
        fetchVerifiedAgents()
      ]);
      setWalletAddress(wallet?.walletAddress || null);
      setHarvestWalletAddress(wallet?.harvestWalletAddress || null);
      setBalanceSol(balance?.balanceSol ?? null);
      setHoldings(tokenHoldings || []);
      setSwaps(swapEvents || []);
      setAgentTvl(tvlSummary);
      setVerifiedAgents(verified || []);
    } finally {
      setRefreshing(false);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawing) return;
    const destination = withdrawAddress.trim();
    if (!destination) {
      setWithdrawStatus('Enter a destination address.');
      return;
    }
    if (!withdrawAll) {
      const amount = Number(withdrawAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setWithdrawStatus('Enter a valid amount.');
        return;
      }
    }

    setWithdrawing(true);
    setWithdrawStatus(null);
    try {
      const response = await withdrawAgentSol({
        toAddress: destination,
        amountSol: withdrawAll ? undefined : Number(withdrawAmount),
        withdrawAll
      });
      setWithdrawStatus(`Withdrawal sent: ${response.signature}`);
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawAll(false);
      await refreshData();
    } catch (error: any) {
      setWithdrawStatus(error?.message || 'Withdraw failed.');
    } finally {
      setWithdrawing(false);
    }
  };

  const visibleHoldings = holdings.filter((holding) => holding.amount > 0.0000015);

  return (
    <PageContainer>
      <Title>Agent Wallet</Title>
      <Card>
        {copiedStatus && <HelperText>{copiedStatus}</HelperText>}
        <Subtle>Agent</Subtle>
        <Value>{agentName ? `${agentName} 🦞` : '—'}</Value>
        <TotalUsdValue>{formatUsd(totalUsd)}</TotalUsdValue>
        <WalletAddressRow>
          <WalletAddressText>{walletAddress || '—'}</WalletAddressText>
        </WalletAddressRow>
        <Subtle style={{ marginTop: 6, textAlign: 'center' }}>Harvest Wallet</Subtle>
        <WalletAddressRow>
          <WalletAddressText>{harvestWalletAddress || '—'}</WalletAddressText>
        </WalletAddressRow>
        <WalletAddressActions>
          <WalletAddressAction type="button" onClick={() => copyAddress(walletAddress, 'Wallet')}>
            Copy Wallet
          </WalletAddressAction>
          <WalletAddressAction type="button" onClick={() => copyAddress(harvestWalletAddress, 'Harvest')}>
            Copy Harvest
          </WalletAddressAction>
        </WalletAddressActions>
        <BalanceRow>
          <BalanceValue>{balanceSol === null ? '—' : `${formatAmount(balanceSol)} SOL`}</BalanceValue>
          <BalanceUsd>
            {balanceSol === null || solPriceUsd === null
              ? '—'
              : `$${(balanceSol * solPriceUsd).toFixed(2)}`}
          </BalanceUsd>
        </BalanceRow>
        <ActionRow>
          <ActionButton type="button" onClick={() => copyAddress(walletAddress, 'Wallet')}>
            <ActionIcon src="/sentry-wallet-fund.png" alt="Fund" />
            Fund
          </ActionButton>
          <ActionButton type="button" onClick={() => setWithdrawOpen((prev) => !prev)}>
            <ActionIcon src="/sentry-wallet-withdraw.png" alt="Withdraw" />
            Withdraw
          </ActionButton>
          <ActionButton type="button" onClick={refreshData} disabled={refreshing}>
            <ActionIcon src="/sentry-wallet-refresh.png" alt="Refresh" />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </ActionButton>
        </ActionRow>
        {withdrawOpen && (
          <div style={{ marginTop: 16 }}>
            <Subtle>Withdraw SOL</Subtle>
            <Input
              placeholder="Destination address"
              value={withdrawAddress}
              onChange={(event) => setWithdrawAddress(event.target.value)}
              style={{ marginTop: 8 }}
            />
            <Row style={{ marginTop: 8, alignItems: 'center' }}>
              <Input
                placeholder="Amount (SOL)"
                value={withdrawAmount}
                onChange={(event) => setWithdrawAmount(event.target.value)}
                disabled={withdrawAll}
              />
            </Row>
            <Row style={{ marginTop: 8, alignItems: 'center' }}>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={withdrawAll}
                  onChange={(event) => setWithdrawAll(event.target.checked)}
                />
                Withdraw all
              </CheckboxRow>
            </Row>
            <Row style={{ marginTop: 10, justifyContent: 'flex-end' }}>
              <WalletAddressAction type="button" onClick={() => setWithdrawOpen(false)}>
                Cancel
              </WalletAddressAction>
              <WalletAddressAction type="button" onClick={handleWithdraw}>
                {withdrawing ? 'Withdrawing…' : 'Send'}
              </WalletAddressAction>
            </Row>
            {withdrawStatus && <HelperText>{withdrawStatus}</HelperText>}
          </div>
        )}
      </Card>

      <Card>
        <SectionLabel>Agent Economy</SectionLabel>
        {loading ? (
          <EmptyState>Loading agent economy…</EmptyState>
        ) : (
          <List>
            <ListItem>
              <Label>Agent TVL</Label>
              <Value>{formatUsd(agentTvl?.totalValueUsd)}</Value>
            </ListItem>
            <ListItem>
              <Label>Agent TMS</Label>
              <Value>{formatUsd(agentTvl?.totalMarketSizeUsd)}</Value>
            </ListItem>
          </List>
        )}
      </Card>

      <Card>
        <SectionLabel>Token Holdings</SectionLabel>
        {loading ? (
          <EmptyState>Loading holdings…</EmptyState>
        ) : visibleHoldings.length === 0 ? (
          <EmptyState>No agent token holdings yet.</EmptyState>
        ) : (
          <List>
            {visibleHoldings.map((holding) => {
                const symbol = holding.symbol || holding.name || 'TOKEN';
                const currentPrice = tokenPricesUsd[holding.mintAddress] || 0;
                const usdValue = holding.amount * currentPrice;
                const roiPct = holding.entryPriceUsd && currentPrice
                  ? ((currentPrice - holding.entryPriceUsd) / holding.entryPriceUsd) * 100
                  : null;
                return (
                  <ListItem key={holding.mintAddress}>
                    <HoldingLeft>
                      <HoldingLogo
                        src={holding.logoUrl || '/token-placeholder.png'}
                        alt={`${symbol} logo`}
                        onError={(event) => {
                          (event.target as HTMLImageElement).src = '/token-placeholder.png';
                        }}
                      />
                      <HoldingSymbol>{symbol}</HoldingSymbol>
                    </HoldingLeft>
                    <HoldingRight>
                      <HoldingAmount>
                        {holding.amount.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                      </HoldingAmount>
                      <HoldingUsd>${usdValue.toFixed(2)}</HoldingUsd>
                      <HoldingRoi $positive={roiPct === null ? null : roiPct >= 0}>
                        {roiPct !== null ? `${roiPct >= 0 ? '+' : ''}${roiPct.toFixed(1)}%` : '—'}
                      </HoldingRoi>
                    </HoldingRight>
                  </ListItem>
                );
            })}
          </List>
        )}
      </Card>

      <Card>
        <HarvestHeader>
          <SectionLabel>Recent Swaps</SectionLabel>
          <HarvestToggle type="button" onClick={() => setRecentSwapsExpanded(!recentSwapsExpanded)}>
            {recentSwapsExpanded ? 'Hide' : 'Show More'}
          </HarvestToggle>
        </HarvestHeader>
        {recentSwapsExpanded && (
          <>
            {loading ? (
              <EmptyState>Loading swaps…</EmptyState>
            ) : swaps.length === 0 ? (
              <EmptyState>No swaps recorded yet.</EmptyState>
            ) : (
              <List>
                {swaps.slice(0, 20).map((swap) => {
                  const amountText = formatSwapTokenAmount(swap);
                  const symbolText = swap.token_symbol || holdingsByMint.get(swap.mint_address)?.symbol || 'TOKEN';
                  const usdValue = solPriceUsd !== null && swap.sol_amount !== null && swap.sol_amount !== undefined
                    ? `$${((swap.sol_amount || 0) * solPriceUsd).toFixed(2)}`
                    : '—';
                  return (
                    <ListItem key={swap.id}>
                      <div>
                        <SwapLabel $direction={swap.direction}>
                          {swap.direction.toUpperCase()}
                          {amountText ? ` • ${amountText}` : ''}
                          {` ${symbolText}`}
                        </SwapLabel>
                      </div>
                      <div>{usdValue}</div>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
        {!recentSwapsExpanded && (
          <HelperText>Show more to view recent swaps.</HelperText>
        )}
      </Card>

      <Card>
        <SectionLabel>Verified Agents 🦞</SectionLabel>
        {loading ? (
          <EmptyState>Loading verified agents…</EmptyState>
        ) : verifiedAgents.length === 0 ? (
          <EmptyState>No verified agents yet.</EmptyState>
        ) : (
          <List>
            {verifiedAgents.map((agent) => (
              <ListItem key={agent.agentName}>
                <div>
                  <Value>{`${agent.agentName} 🦞`}</Value>
                  {agent.moltbookHandle && <Label>@{agent.moltbookHandle}</Label>}
                </div>
                <Label>{agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : ''}</Label>
              </ListItem>
            ))}
          </List>
        )}
      </Card>
    </PageContainer>
  );
};

export default SentryAgentWalletPWA;
